import React, { useEffect, useState, useRef } from "react";
import Styles from "../../assets/css/components/scanBarcode/scanBarcode.module.scss";
import { IoClose } from "react-icons/io5";
import Inbound from '../../assets/images/Dashboard/inbound.png';
import Outbound from "../../assets/images/Dashboard/outbound.png";
import Scanner from "../../assets/images/scanner.png";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import BarcodeScannerComponent from "react-qr-barcode-scanner";
import { getProductByBarcodeNumber } from "../../services/Api";
import { useDispatch } from "react-redux";
import { popUpBoxTrigger } from "../../features/message/popUpSlice";

const ScanBarcode = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { state } = location;
  const navigate = useNavigate();
  const { transactionId } = useParams();
  const [scannerType, setScannerType] = useState(state?.scanType || null);
  const [scannedBarcode, setScannedBarcode] = useState("");
  const [scanningError, setScanningError] = useState({
    camera: false,
    device: false
  });
  const [scanTimer, setScanTimer] = useState(null);
  const [loading, setLoading] = useState(false);

  // Get existing products from state if available
  const existingProducts = state?.existingProducts || [];
  const isOutbound = state?.isOutbound || false;

  // Store previous product details to return to
  const previousProductData = state?.previousProductData || null;
  const returnToDetailsOnClose = state?.returnToDetailsOnClose || false;

  const barcodeBuffer = useRef("");
  const bufferClearTimer = useRef(null);

  const showPopup = (type, heading, message) => {
    dispatch(popUpBoxTrigger({
      type,
      heading,
      message,
      show: true
    }));
  };

  const handleInactiveProduct = (message) => {
    setScannerType(null);
    setLoading(false);
    setTimeout(() => {
      showPopup('error', 'Error', message);
    }, 100);
  };

  const handleScannedResult = async (barcode) => {
    if (!barcode) {
      setScanningError(prev => ({
        ...prev,
        device: true
      }));
      return;
    }

    setLoading(true);
    setScanningError({ camera: false, device: false });

    try {
      // Get product details
      const response = await getProductByBarcodeNumber(barcode);

      if (response.status === 200 && response.data?.id) {
        const scannedProduct = response.data;
        const productId = scannedProduct.id;

        // FIXED: Check if product already exists in current transaction
        if (existingProducts && existingProducts.length > 0) {
          const existingIndex = existingProducts.findIndex(p => p.id === productId);

          if (existingIndex >= 0) {
            // Product already exists in transaction - DO NOT NAVIGATE
            const productName = scannedProduct.product_name || 'Product';

            // Show popup but DON'T navigate
            showPopup('info', 'Product Already Added',
              `${productName} is already in the transaction. You can increase its quantity directly.`);

            setScannerType(null);

            // Navigate back to product details with the existing product selected
            setTimeout(() => {
              navigate(`/products/details/${transactionId || "new"}`, {
                state: {
                  existingProducts: existingProducts,
                  duplicateProductId: productId,
                  selectedProductId: productId,
                  isOutbound: scannerType === "outbound" || isOutbound,
                  isInbound: scannerType === "inbound" && !isOutbound,
                  message: `"${productName}" is already in the transaction.`
                }
              });
            }, 1500);
            setLoading(false);
            return;
          }
        }

        // Continue with original logic if product is not a duplicate
        if (scannerType === "inbound" && !isOutbound) {
          try {
            const response = await getProductByBarcodeNumber(barcode);

            if (response.status === 200 && response.data?.id) {
              const existingProduct = response.data;
              setScannerType(null);

              let updatedProducts = [];
              const targetTransactionId = existingProduct.transaction_id || transactionId || "new";

              if (existingProducts && existingProducts.length > 0) {
                updatedProducts = [...existingProducts];

                // FIX: For inbound, use available quantity as base
                const availableQuantity = parseInt(existingProduct.quantity) || 0;
                const newProduct = {
                  id: existingProduct.id,
                  product_name: existingProduct.product_name,
                  sku_number: existingProduct.sku_number,
                  quantity: availableQuantity,
                  brand_name: existingProduct.brand_name,
                  description: existingProduct.description,
                  barcode_number: existingProduct.barcode_number,
                  transaction_id: existingProduct.transaction_id || transactionId || null,
                  originalDbQuantity: availableQuantity,
                  lazada_product: existingProduct.lazada_product // IMPORTANT: Include SKU data
                };
                updatedProducts.unshift(newProduct);
              } else {
                // First product in cart
                const availableQuantity = parseInt(existingProduct.quantity) || 0;
                updatedProducts = [{
                  id: existingProduct.id,
                  product_name: existingProduct.product_name,
                  sku_number: existingProduct.sku_number,
                  quantity: availableQuantity,
                  brand_name: existingProduct.brand_name,
                  description: existingProduct.description,
                  barcode_number: existingProduct.barcode_number,
                  transaction_id: existingProduct.transaction_id || transactionId || null,
                  originalDbQuantity: availableQuantity,
                  lazada_product: existingProduct.lazada_product // IMPORTANT: Include SKU data
                }];
              }

              navigate(`/products/details/${targetTransactionId}`, {
                state: {
                  productData: {
                    id: existingProduct.id,
                    product_name: existingProduct.product_name,
                    sku_number: existingProduct.sku_number,
                    quantity: parseInt(existingProduct.quantity) || 0,
                    brand_name: existingProduct.brand_name,
                    description: existingProduct.description,
                    barcode_number: existingProduct.barcode_number,
                    transaction_id: existingProduct.transaction_id,
                    originalDbQuantity: parseInt(existingProduct.quantity) || 0,
                    lazada_product: existingProduct.lazada_product, // IMPORTANT
                    ...existingProduct
                  },
                  isInbound: true,
                  barcode,
                  autoRedirected: true,
                  existingProducts: updatedProducts,
                  selectedProductId: existingProduct.id
                }
              });
            } else {
              setScannerType(null);
              if (transactionId && transactionId !== 'new') {
                navigate(`/products/add/${transactionId}`, {
                  state: {
                    barcode,
                    autoRedirected: true
                  }
                });
              } else {
                navigate(`/products/add`, {
                  state: {
                    barcode,
                    autoRedirected: true
                  }
                });
              }
            }
          } catch (error) {
            console.error("Inbound scan error:", error);
            setScannerType(null);

            if (error.response?.status === 422 && error.response.data?.message) {
              handleInactiveProduct(error.response.data.message);
              return;
            }

            if (transactionId && transactionId !== 'new') {
              navigate(`/products/add/${transactionId}`, {
                state: {
                  barcode,
                  autoRedirected: true
                }
              });
            } else {
              navigate(`/products/add`, {
                state: {
                  barcode,
                  autoRedirected: true
                }
              });
            }
          }
          finally {
            setLoading(false);
          }
          return;
        }

        // Outbound or fallback flows
        if ((scannerType === "outbound" || isOutbound) && (!scannedProduct.quantity || scannedProduct.quantity <= 0)) {
          setScannerType(null);
          setTimeout(() => {
            showPopup('error', 'Error', 'Selected Product is out of stock!');
          }, 100);
          return;
        }

        setScannerType(null);

        if (scannerType === "outbound" || isOutbound) {
          if (existingProducts && existingProducts.length > 0) {
            const updatedProducts = [...existingProducts];

            // Add new product to the beginning of the list
            if (!scannedProduct.quantity || scannedProduct.quantity <= 0) {
              showPopup('error', 'Error', `${scannedProduct.product_name} is out of stock!`);
              return;
            }

            updatedProducts.unshift({
              id: scannedProduct.id,
              product_name: scannedProduct.product_name,
              sku_number: scannedProduct.sku_number,
              quantity: 1,
              brand_name: scannedProduct.brand_name,
              description: scannedProduct.description,
              barcode_number: scannedProduct.barcode_number,
              lazada_product: scannedProduct.lazada_product // IMPORTANT: Include SKU data
            });

            navigate(`/products/details/${transactionId || "new"}`, {
              state: {
                existingProducts: updatedProducts,
                productData: {
                  ...scannedProduct,
                  originalDbQuantity: scannedProduct.quantity
                },
                isOutbound: true,
                autoRedirected: true,
                selectedProductId: scannedProduct.id
              }
            });
          } else {
            if (!scannedProduct.quantity || scannedProduct.quantity <= 0) {
              showPopup('error', 'Error', `${scannedProduct.product_name} is out of stock!`);
              return;
            }

            navigate(`/products/details/${scannedProduct.product_transaction?.transaction_id || transactionId || "new"}`, {
              state: {
                existingProducts: [{
                  id: scannedProduct.id,
                  product_name: scannedProduct.product_name,
                  sku_number: scannedProduct.sku_number,
                  quantity: 1,
                  brand_name: scannedProduct.brand_name,
                  description: scannedProduct.description,
                  barcode_number: scannedProduct.barcode_number,
                  lazada_product: scannedProduct.lazada_product // IMPORTANT: Include SKU data
                }],
                productData: {
                  ...scannedProduct,
                  originalDbQuantity: scannedProduct.quantity
                },
                barcode,
                isOutbound: true,
                autoRedirected: true,
                selectedProductId: scannedProduct.id
              }
            });
          }
        } else {
          // General product scan (not inbound/outbound specific)
          navigate(transactionId ? `/products/add/${transactionId}` : `/products/add`, {
            state: {
              barcode,
              productData: scannedProduct,
              autoRedirected: true
            }
          });
        }
      } else {
        setScannerType(null);

        if (scannerType === "outbound" || isOutbound) {
          setTimeout(() => {
            showPopup('error', 'Error', 'Product not found with this barcode');
          }, 100);

          setTimeout(() => {
            if (existingProducts && existingProducts.length > 0) {
              navigate(`/products/details/${transactionId || "new"}`, {
                state: {
                  existingProducts: existingProducts,
                  productData: previousProductData || existingProducts[0],
                  isOutbound: true,
                  autoRedirected: true,
                  selectedProductId: previousProductData?.id || existingProducts[0]?.id
                }
              });
            } else {
              navigate(`/scanBarcode/${transactionId || 'new'}`, {
                state: {
                  scanType: "outbound",
                  isOutbound: true
                }
              });
            }
          }, 2000);
        } else {
          handleManualScan(barcode);
        }
      }
    } catch (error) {
      console.error("Error fetching product:", error);
      setScannerType(null);

      if (error.response?.status === 422 && error.response.data?.message) {
        handleInactiveProduct(error.response.data.message);
        return;
      } else {
        setTimeout(() => {
          showPopup('error', 'Error', 'Product not found with this barcode');
        }, 100);
      }

      if (scannerType === "inbound" && !isOutbound) {
        handleManualScan(barcode);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleManualScan = (barcode) => {
    if (transactionId && transactionId !== 'new') {
      // We have an existing transaction
      navigate(`/products/add/${transactionId}`, {
        state: {
          barcode,
          autoRedirected: true
        }
      });
    } else {
      // First product - no transaction yet
      navigate(`/products/add`, {
        state: {
          barcode,
          autoRedirected: true
        }
      });
    }
  };

  const handleScannerClose = () => {
    setScannerType(null);
    setScanningError({ camera: false, device: false }); // Clear any errors when closing
    setScannedBarcode(""); // Clear scanned barcode

    if (returnToDetailsOnClose && transactionId) {
      navigate(`/products/details/${transactionId}`, {
        state: {
          existingProducts,
          productData: previousProductData,
          isNewProduct: true,
          isOutbound,
          returnedFromScanner: true
        }
      });
    }
  };

  // Handle Device Barcode Input
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Only process keystrokes when scanner is open
      if (!scannerType) return;

      setScanningError(prev => ({
        ...prev,
        device: false
      }));

      if (bufferClearTimer.current) clearTimeout(bufferClearTimer.current);

      if (e.key === "Enter") {
        const finalBarcode = barcodeBuffer.current.trim();
        barcodeBuffer.current = "";
        if (finalBarcode) {
          setScannedBarcode(finalBarcode);
          handleScannedResult(finalBarcode);
        }
      } else if (e.key.length === 1 && e.key !== " ") {
        // Only add printable characters (excluding space) to the buffer
        barcodeBuffer.current += e.key;
        bufferClearTimer.current = setTimeout(() => {
          barcodeBuffer.current = "";
        }, 300); // Clear after 300ms of inactivity
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => {
      window.removeEventListener("keydown", handleKeyPress);
      if (bufferClearTimer.current) clearTimeout(bufferClearTimer.current);
    };
  }, [scannerType, existingProducts, isOutbound]);

  useEffect(() => {
    // Auto-open scanner if specified in the state
    if (state?.scanType && state.autoOpenScanner) {
      setScannerType(state.scanType);
    }

    return () => {
      if (scanTimer) clearTimeout(scanTimer);
      if (bufferClearTimer.current) clearTimeout(bufferClearTimer.current);
    };
  }, [state]);

  // Clear errors when scanner type changes
  useEffect(() => {
    if (!scannerType) return;

    setScanningError({
      camera: false,
      device: true   // assume NOT connected
    });
  }, [scannerType]);

  return (
    <div className={`${Styles.scanner}`}>
      <div className={`${Styles.scanner_heading}`}>
        <h2>Scan Barcode</h2>
      </div>

      <div className={`${Styles.stock}`}>
        {/* Inbound Box */}
        <div className={`${Styles.box}`}>
          <div className={`${Styles.inbound_stock}`}>
            <div className={`${Styles.inbound}`} onClick={() => setScannerType("inbound")}>
              <p>INBOUND STOCK</p>
              <img src={Inbound} alt="INBOUND STOCK" />
            </div>
          </div>
          <button
            className={`${Styles.inbound_button}`}
            onClick={() => setScannerType("inbound")}
            disabled={isOutbound}
          >
            <img src={Scanner} alt="scanner" /> Scan
          </button>
        </div>

        {/* Outbound Box */}
        <div className={`${Styles.box}`}>
          <div className={`${Styles.outbound_stock}`}>
            <div className={`${Styles.outbound}`} onClick={() => setScannerType("outbound")}>
              <p>OUTBOUND STOCK</p>
              <img src={Outbound} alt="OUTBOUND STOCK" />
            </div>
          </div>
          <button
            className={`${Styles.outbound_button}`}
            onClick={() => setScannerType("outbound")}
            disabled={!isOutbound && transactionId && transactionId !== "new"}
          >
            <img src={Scanner} alt="scanner" /> Scan
          </button>
        </div>
      </div>

      {/* Scanner Pop-up (Camera-based) */}
      {scannerType && (
        <div className={`${Styles.scanner_container}`}>
          <div className={`${Styles.scanner_box}`}>
            <button className={`${Styles.close_button}`} onClick={handleScannerClose}>
              <IoClose />
            </button>
            <div className={`${Styles.scan_cam}`}>
              <BarcodeScannerComponent
                onUpdate={(err, result) => {
                  if (err) {
                    // Don't set error here as it's too noisy
                    // console.warn("Scanner update error:", err);
                  } else if (result) {
                    setScannedBarcode(result.text);
                    handleScannedResult(result.text);
                  } else {
                    setScannedBarcode("");
                  }
                }}
                onError={(err) => {
                  console.error("Scanner error:", err);
                  setScanningError(prev => ({
                    ...prev,
                    camera: true
                  }));
                }}
              />
            </div>
            <div className={`${Styles.scanner_buttons}`}>
              <button
                className={`${scannerType === "inbound" ? Styles.scan_button_green : Styles.scan_button_red}`}
                onClick={() => handleScannedResult(scannedBarcode)}
                disabled={!scannedBarcode || loading}
              >
                <img src={Scanner} alt="scanner" />
                {/* {loading && <span style={{marginLeft: '5px'}}>Processing...</span>} */}
              </button>
              <div className={`${Styles.barcode_manual}`}>
                {scannerType === "inbound" && !isOutbound && (
                  <button onClick={() => {
                    setScannerType(null);
                    handleManualScan(scannedBarcode);
                  }}>
                    + Add Manually
                  </button>
                )}
              </div>
            </div>
            {(scanningError.camera || scanningError.device) && (
              <div className={`${Styles.scan_error}`}>
                {scanningError.camera && <div>⚠️ Camera permission not allowed.</div>}
                {/* {scanningError.device && <div>⚠️ Barcode scanner device not connected.</div>} */}
              </div>
            )}
            {/* {scannedBarcode && (
              <div className={`${Styles.scanned_code}`} style={{
                marginTop: '10px', 
                padding: '8px', 
                background: '#f0f0f0', 
                borderRadius: '4px',
                fontSize: '14px',
                textAlign: 'center'
              }}>
                Scanned: {scannedBarcode}
              </div>
            )} */}
          </div>
        </div>
      )}

      {/* Debug section - Only for development purposes */}
      {/* Uncomment for debugging
      <div style={{margin: '20px', padding: '10px', background: '#f5f5f5', borderRadius: '5px'}}>
        <h4>Debug Info:</h4>
        <p>Scanner Type: {scannerType}</p>
        <p>Transaction ID: {transactionId}</p>
        <p>Is Outbound: {isOutbound ? 'Yes' : 'No'}</p>
        <p>Existing Products: {existingProducts.length}</p>
        <p>Scanned Barcode: {scannedBarcode}</p>
      </div>
      */}
    </div>
  );
};

export default ScanBarcode;
